import { Alert, DisplayUserPresentation, FormTemplate, FormTextField, Modal, TinyButton, tokens } from 'ComponentLibrary'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { AppraiserAssignmentModalFormInput } from 'ComponentLibrary/src/widgets/AppraiserAssignment/types'
import { GridLegacy as Grid, Stack } from '@mui/material'
import { UsersAutocomplete } from 'ComponentLibrary/src/widgets/Participants/UserAutocomplete'
import { PlusIcon, XIcon } from '@phosphor-icons/react'
import { useContext, useMemo, useState } from 'react'
import { IconButton } from 'ComponentLibrary/src/piazza/buttons'
import { type AppraiserAssignment as AppraiserAssignmentType, User } from 'ComponentLibrary/src/common/users'
import { Typography } from 'ComponentLibrary/src/piazza'
import type { UserPresentation } from 'ComponentLibrary/src/common/userPresentations'
import type { UserLinkCopy } from 'ComponentLibrary/src/piazza/text/UserLink/UserLink'
import { validate as isUuid } from 'uuid'
import { PiazzaInputContext } from 'ComponentLibrary/src/molecules/Form/PiazzaInputContext'

type AppraiserAssignmentProps = {
    open: boolean
    onClose: () => void
    onSubmitCallback: (data: AppraiserAssignmentType | undefined) => void
    appraiserAssignment?: AppraiserAssignmentModalFormInput
    users?: User[]
    primaryButtonLabel: string
    isEditingAppraiserAssignment?: boolean
    copy?: Partial<AppraiserAssignmentModalCopy>
    userLinkCopy?: Partial<UserLinkCopy>
}

export type AppraiserAssignmentModalCopy = {
    customOptionName: string
    customOptionGroup: string
    title: string
    cancel: string
    duplicateWarning: string
    customInfo: string
    mainAppraiser: string
    secondaryAppraiser: string
    tertiaryAppraiser: string
    nameAndCertification: string
    remove: string
    addSecondary: string
    addTertiary: string
}

const DEFAULT_APPRAISER_ASSIGNMENT_MODAL_COPY: AppraiserAssignmentModalCopy = {
    customOptionName: '► Extern (Freitext)',
    customOptionGroup: 'Nicht registrierte Gutachter',
    title: 'Zuständige Gutachter/innen',
    cancel: 'Abbrechen',
    duplicateWarning: 'Derselbe Gutachter wurde mehrfach ausgewählt. Bitte ändern Sie einen der Einträge.',
    customInfo:
        'Freitext bitte nur für nicht registrierte Gutachter verwenden. Automatisierungen funktionieren nur mit verknüpften Gutachtern.',
    mainAppraiser: 'Erstgutachter/in',
    secondaryAppraiser: 'Zweitgutachter/in',
    tertiaryAppraiser: 'Drittgutachter/in',
    nameAndCertification: 'Name und Zertifizierung',
    remove: 'Entfernen',
    addSecondary: 'Zweitgutachter/in hinzufügen',
    addTertiary: 'Drittgutachter/in hinzufügen',
}

const findUserByUuid = (users: User[] | undefined, value?: string) =>
    value ? users?.find((user) => user.uuid.toLowerCase() === value.toLowerCase()) : undefined

const getAssignmentFieldDefaults = (value: string | undefined, users: User[] | undefined) => {
    if (!value) {
        return { selection: undefined, custom: undefined }
    }

    if (findUserByUuid(users, value) || isUuid(value)) {
        return { selection: value, custom: undefined }
    }

    return { selection: 'custom', custom: value }
}

const getAssignmentFormDefaults = (
    assignment: AppraiserAssignmentModalFormInput | undefined,
    users: User[] | undefined
): AppraiserAssignmentModalFormInput => {
    const main = getAssignmentFieldDefaults(assignment?.main, users)
    const secondary = getAssignmentFieldDefaults(assignment?.secondary, users)
    const tertiary = getAssignmentFieldDefaults(assignment?.tertiary, users)

    return {
        main: main.selection,
        mainCustom: main.custom,
        secondary: secondary.selection,
        secondaryCustom: secondary.custom,
        tertiary: tertiary.selection,
        tertiaryCustom: tertiary.custom,
    }
}

const getAssignmentPresentation = (value: string, users: User[] | undefined, rank: 'main' | 'secondary' | 'tertiary'): UserPresentation => {
    const user = findUserByUuid(users, value)
    return user
        ? { kind: 'account', key: `appraiser:${rank}:${user.uuid.toLowerCase()}`, displayData: user }
        : { kind: 'unavailable', key: `appraiser:${rank}:${value.toLowerCase()}` }
}

export function AppraiserAssignmentModal({
    open,
    onClose,
    onSubmitCallback,
    appraiserAssignment,
    users,
    isEditingAppraiserAssignment,
    primaryButtonLabel,
    copy,
    userLinkCopy,
}: AppraiserAssignmentProps) {
    const usePiazzaInputs = useContext(PiazzaInputContext)
    const resolvedCopy = { ...DEFAULT_APPRAISER_ASSIGNMENT_MODAL_COPY, ...copy }
    const defaultValues = getAssignmentFormDefaults(appraiserAssignment, users)
    const [isSecondaryAppraiserVisible, setIsSecondaryAppraiserVisible] = useState(!!appraiserAssignment?.secondary)
    const [isTertiaryAppraiserVisible, setIsTertiaryAppraiserVisible] = useState(!!appraiserAssignment?.tertiary)
    const {
        control,
        handleSubmit,
        formState: { errors, isDirty },
        reset,
        setValue,
    } = useForm<AppraiserAssignmentModalFormInput>({ defaultValues })

    const [main, secondary, tertiary] = useWatch({ control, name: ['main', 'secondary', 'tertiary'] })

    const hasDuplicateUsers = useMemo(() => {
        const appraisers = [main, secondary, tertiary].filter((appraiser) => appraiser && appraiser !== 'custom')
        return new Set(appraisers).size !== appraisers.length
    }, [main, secondary, tertiary])

    const onSubmit = handleSubmit((data) => {
        if (!data.main || (data.main === 'custom' && !data.mainCustom)) {
            onSubmitCallback(undefined)
        } else {
            onSubmitCallback({
                main: (data.main === 'custom' ? data.mainCustom || undefined : data.main) as string,
                secondary: data.secondary === 'custom' ? data.secondaryCustom || undefined : data.secondary || undefined,
                tertiary: data.tertiary === 'custom' ? data.tertiaryCustom || undefined : data.tertiary || undefined,
            })
        }
    })

    const onCancel = () => {
        setIsSecondaryAppraiserVisible(!!appraiserAssignment?.secondary)
        setIsTertiaryAppraiserVisible(!!appraiserAssignment?.tertiary)
        reset(defaultValues)
        onClose()
    }

    const customOption = {
        uuid: 'custom',
        id: 0,
        first_name: resolvedCopy.customOptionName,
        email: '',
        organization_name: resolvedCopy.customOptionGroup,
    }
    const options = [customOption, ...(users ?? [])]

    return (
        <Modal
            isOpen={open}
            onClose={onCancel}
            title={resolvedCopy.title}
            width="700px"
            showLoading={isEditingAppraiserAssignment}
            footerProps={{
                primaryButton: {
                    text: primaryButtonLabel,
                    onClick: onSubmit,
                    disabled: isEditingAppraiserAssignment || !isDirty || (isSecondaryAppraiserVisible && !main) || hasDuplicateUsers,
                },
                secondaryButton: { text: resolvedCopy.cancel, onClick: onCancel },
            }}
        >
            <Stack direction="column" gap={tokens.SizingSpacing200} width="100%">
                {hasDuplicateUsers && (
                    <Alert size="small" severity="warning">
                        <Typography variant="textS" color={tokens.ColorTextBold}>
                            {resolvedCopy.duplicateWarning}
                        </Typography>
                    </Alert>
                )}
                {(main === 'custom' || secondary === 'custom' || tertiary === 'custom') && (
                    <Alert severity="info" size="small">
                        {resolvedCopy.customInfo}
                    </Alert>
                )}
                <FormTemplate onSubmit={onSubmit} onCancel={onCancel} isFormDirty={isDirty} hideFooter dense={usePiazzaInputs}>
                    <Grid item xs={5.5}>
                        <Controller
                            control={control}
                            name="main"
                            render={({ field: { onChange, value } }) => (
                                <UsersAutocomplete
                                    required
                                    users={options}
                                    value={findUserByUuid(options, value) || null}
                                    label={resolvedCopy.mainAppraiser}
                                    onChange={(user) => onChange(user?.uuid || null)}
                                    customOption={customOption}
                                    userLinkCopy={userLinkCopy}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={5.5} sx={{ alignSelf: 'center' }}>
                        {main === 'custom' ? (
                            <FormTextField
                                required
                                control={control}
                                label={resolvedCopy.nameAndCertification}
                                fieldName="mainCustom"
                                errors={errors}
                                fullWidth
                            />
                        ) : (
                            main &&
                            main !== 'custom' && (
                                <DisplayUserPresentation
                                    presentation={getAssignmentPresentation(main, users, 'main')}
                                    size="medium"
                                    copy={userLinkCopy}
                                />
                            )
                        )}
                    </Grid>
                    <Grid item xs={1} sx={{ alignSelf: 'center' }}>
                        {main && (
                            <IconButton
                                icon={<XIcon />}
                                tooltip={resolvedCopy.remove}
                                size="small"
                                variant="tertiary"
                                onClick={() => {
                                    setValue('main', '', { shouldDirty: true })
                                    setValue('mainCustom', '', { shouldDirty: true })
                                }}
                            />
                        )}
                    </Grid>
                    {isSecondaryAppraiserVisible ? (
                        <>
                            <Grid item xs={5.5}>
                                <Controller
                                    control={control}
                                    name="secondary"
                                    render={({ field: { onChange, value } }) => (
                                        <UsersAutocomplete
                                            required
                                            users={options}
                                            value={findUserByUuid(options, value) || null}
                                            label={resolvedCopy.secondaryAppraiser}
                                            onChange={(user) => onChange(user?.uuid || null)}
                                            customOption={customOption}
                                            userLinkCopy={userLinkCopy}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={5.5} sx={{ alignSelf: 'center' }}>
                                {secondary === 'custom' ? (
                                    <FormTextField
                                        required
                                        control={control}
                                        label={resolvedCopy.nameAndCertification}
                                        fieldName="secondaryCustom"
                                        errors={errors}
                                        fullWidth
                                    />
                                ) : (
                                    secondary &&
                                    secondary !== 'custom' && (
                                        <DisplayUserPresentation
                                            presentation={getAssignmentPresentation(secondary, users, 'secondary')}
                                            size="medium"
                                            copy={userLinkCopy}
                                        />
                                    )
                                )}
                            </Grid>
                            <Grid item xs={1} sx={{ alignSelf: 'center' }}>
                                <IconButton
                                    icon={<XIcon />}
                                    size="small"
                                    tooltip={resolvedCopy.remove}
                                    variant="tertiary"
                                    onClick={() => {
                                        setIsSecondaryAppraiserVisible(false)
                                        setIsTertiaryAppraiserVisible(false)
                                        setValue('secondary', '', { shouldDirty: true })
                                        setValue('secondaryCustom', '', { shouldDirty: true })
                                        setValue('tertiary', '', { shouldDirty: true })
                                        setValue('tertiaryCustom', '', { shouldDirty: true })
                                    }}
                                />
                            </Grid>
                            {isTertiaryAppraiserVisible ? (
                                <>
                                    <Grid item xs={5.5}>
                                        <Controller
                                            control={control}
                                            name="tertiary"
                                            render={({ field: { onChange, value } }) => (
                                                <UsersAutocomplete
                                                    required
                                                    users={options}
                                                    value={findUserByUuid(options, value) || null}
                                                    label={resolvedCopy.tertiaryAppraiser}
                                                    onChange={(user) => onChange(user?.uuid || null)}
                                                    customOption={customOption}
                                                    userLinkCopy={userLinkCopy}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={5.5} sx={{ alignSelf: 'center' }}>
                                        {tertiary === 'custom' ? (
                                            <FormTextField
                                                required
                                                control={control}
                                                label={resolvedCopy.nameAndCertification}
                                                fieldName="tertiaryCustom"
                                                errors={errors}
                                                fullWidth
                                            />
                                        ) : (
                                            tertiary &&
                                            tertiary !== 'custom' && (
                                                <DisplayUserPresentation
                                                    presentation={getAssignmentPresentation(tertiary, users, 'tertiary')}
                                                    size="medium"
                                                    copy={userLinkCopy}
                                                />
                                            )
                                        )}
                                    </Grid>
                                    <Grid item xs={1} sx={{ alignSelf: 'center' }}>
                                        <IconButton
                                            icon={<XIcon />}
                                            size="small"
                                            tooltip={resolvedCopy.remove}
                                            variant="tertiary"
                                            onClick={() => {
                                                setIsTertiaryAppraiserVisible(false)
                                                setValue('tertiary', '', { shouldDirty: true })
                                                setValue('tertiaryCustom', '', { shouldDirty: true })
                                            }}
                                        />
                                    </Grid>
                                </>
                            ) : (
                                <Grid item xs={12}>
                                    <TinyButton
                                        label={resolvedCopy.addTertiary}
                                        leftIcon={<PlusIcon />}
                                        onClick={() => setIsTertiaryAppraiserVisible(true)}
                                    />
                                </Grid>
                            )}
                        </>
                    ) : (
                        <Grid item xs={12}>
                            <TinyButton
                                label={resolvedCopy.addSecondary}
                                leftIcon={<PlusIcon />}
                                onClick={() => setIsSecondaryAppraiserVisible(true)}
                            />
                        </Grid>
                    )}
                </FormTemplate>
            </Stack>
        </Modal>
    )
}
